/**
 * Step-up 2FA in place (2026-10 browser QA, payroll #11). Import-free so
 * `node --test` can load it.
 *
 * When a strict-TOTP route answers 401 totp_required, the response
 * interceptor (client.ts) asks the registered requester — the StepUpPrompt
 * dialog mounted in the workspace layout — for the authenticator code, and
 * on success replays the original request exactly once. Concurrent
 * totp_required responses share ONE prompt. Without a requester (outside
 * the workspace layout) or when the user cancels, the request is rejected
 * as before and the caller keeps its own fallback.
 */

/** Resolves true once the second factor (passkey or authenticator code)
 *  was verified, false when cancelled. */
export type StepUpRequester = () => Promise<boolean>;

let requester: StepUpRequester | null = null;
let inFlight: Promise<boolean> | null = null;

/** Registers (or, with null, removes) the prompt. */
export function setStepUpRequester(fn: StepUpRequester | null): void {
  requester = fn;
}

/** True when a 401 should open the step-up prompt: the code is
 *  totp_required, the request has not already been replayed after a
 *  step-up, and a prompt is mounted. */
export function shouldStepUp(errorCode: unknown, alreadyReplayed: boolean): boolean {
  return errorCode === "totp_required" && !alreadyReplayed && requester !== null;
}

/** Asks for the code once for every request waiting on it. Resolves false
 *  when no prompt is mounted, it is cancelled, or it fails. */
export function requestStepUp(): Promise<boolean> {
  if (!requester) return Promise.resolve(false);
  if (!inFlight) {
    inFlight = requester()
      .catch(() => false)
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

/** The error code a cancelled step-up rejects the original request with. */
export const STEP_UP_CANCELLED = "step_up_cancelled";
export const STEP_UP_CANCELLED_MESSAGE =
  "Two-factor verification was cancelled, so nothing was done. Try again when you have your passkey or authenticator app.";

/** Returned by withStepUpRetry when the person cancelled the prompt. */
export interface StepUpDeclined {
  stepUpDeclined: true;
}

export const STEP_UP_DECLINED: StepUpDeclined = Object.freeze({ stepUpDeclined: true as const });

export function isStepUpDeclined(v: unknown): v is StepUpDeclined {
  return typeof v === "object" && v !== null && (v as { stepUpDeclined?: unknown }).stepUpDeclined === true;
}

/**
 * Runs a 2FA-method change (add / remove a passkey or authenticator). When
 * the server answers 403 step_up_required (the person already has a method
 * and this session has no fresh step-up), opens the step-up prompt and
 * retries exactly once. Resolves STEP_UP_DECLINED when the prompt is
 * cancelled; every other error rejects as usual. `codeOf` extracts the API
 * error code (toApiError(err).error) — passed in to keep this file
 * import-free for `node --test`.
 */
export async function withStepUpRetry<T>(
  run: () => Promise<T>,
  codeOf: (err: unknown) => string | undefined,
): Promise<T | StepUpDeclined> {
  try {
    return await run();
  } catch (err) {
    if (codeOf(err) !== "step_up_required") throw err;
    if (!(await requestStepUp())) return STEP_UP_DECLINED;
    return run();
  }
}
